import { ethers } from "ethers";
import { getSigningKey } from "../joinsplit/keys";

export const AVANTIS_DEFAULT_MARKET_SLIPPAGE_X10 = 10n ** 10n; // 1% in Avantis 10-decimal percent units.

const BN254_PRIME = BigInt(
  "21888242871839275222246405745257275088548364400416034343698204186575808495617",
);

export const PERPS_ACTION_DOMAINS = {
  marginDeposit: ethers.id("PRIVATE_PERPS_MARGIN_DEPOSIT"),
  marginWithdraw: ethers.id("PRIVATE_PERPS_MARGIN_WITHDRAW"),
  marginReshield: ethers.id("PRIVATE_PERPS_MARGIN_RESHIELD"),
  commitOrder: ethers.id("PRIVATE_PERPS_COMMIT_ORDER"),
  cancelOrder: ethers.id("PRIVATE_PERPS_CANCEL_ORDER"),
} as const;

type PerpsActionDomain =
  (typeof PERPS_ACTION_DOMAINS)[keyof typeof PERPS_ACTION_DOMAINS];

type MarginUsdcActionConfig = {
  spotMarketProxy: string;
  usdcToken: string;
  sUsdToken: string;
  usdcAmount: bigint;
  synthMarketId: bigint;
  minSUSDAmount: bigint;
  protocolFeeRecipient: string;
  partnerFeeRecipient: string;
  protocolFeeBps: number | bigint;
  partnerFeeBps: number | bigint;
};

type MarginWithdrawUsdcActionConfig = {
  spotMarketProxy: string;
  usdcToken: string;
  sUsdToken: string;
  sUsdAmount: bigint;
  synthMarketId: bigint;
  recipient: string;
  minUSDCAmount: bigint;
};

type MarginReshieldActionConfig = {
  shieldedPool: string;
  usdcToken: string;
  usdcAmount: bigint;
  outputCommitment: string;
  outputTokenHash: string;
  recoveryKey: string;
  encryptedNote: string;
};

type PerpsActionDataHashParams = {
  actionDomain: PerpsActionDomain;
  perpsMarketProxy: string;
  callData: string;
  callValueWei?: bigint | number | string;
  omitCallValueFromActionData?: boolean;
  marginUsdcConfig?: MarginUsdcActionConfig;
  marginWithdrawUsdcConfig?: MarginWithdrawUsdcActionConfig;
  marginReshieldConfig?: MarginReshieldActionConfig;
};

type PerpsProofActionHashParams = PerpsActionDataHashParams & {
  vaultAddress: string;
  chainId: number | bigint;
  nonce: number | bigint;
  expiry: number | bigint;
};

let poseidon: any;
let eddsa: any;

async function ensurePoseidon() {
  if (!poseidon) {
    const circomlibjs = await import("circomlibjs");
    poseidon = await circomlibjs.buildPoseidon();
  }
  return poseidon;
}

async function ensureEddsa() {
  if (!eddsa) {
    const circomlibjs = (await import("circomlibjs")) as unknown as {
      buildEddsa: () => Promise<any>;
    };
    eddsa = await circomlibjs.buildEddsa();
  }
  return eddsa;
}

function normalizeBytes32(input: string): string {
  const trimmed = String(input || "").trim();
  const withPrefix = trimmed.startsWith("0x") ? trimmed : `0x${trimmed}`;
  if (!/^0x[0-9a-fA-F]{64}$/.test(withPrefix)) {
    throw new Error(`Expected bytes32 hex, got: ${input}`);
  }
  return withPrefix.toLowerCase();
}

function reduceToBn254(input: string): bigint {
  return BigInt(normalizeBytes32(input)) % BN254_PRIME;
}

/**
 * The vault's ownership commitment, `Poseidon(Ax, Ay, salt)`.
 *
 * This names the Baby Jubjub key allowed to authorise vault actions, which is
 * what `circuits/perps_ownership_auth.circom` opens. It is deliberately NOT
 * over `mpk`: `mpk` is a digest derived from that key, so committing to it left
 * the circuit with no opening it could ever satisfy.
 */
export async function computePerpsOwnershipCommitment(
  publicKeyX: bigint,
  publicKeyY: bigint,
  saltHex: string,
): Promise<string> {
  const p = await ensurePoseidon();
  const salt = reduceToBn254(saltHex);
  const hash = p([publicKeyX % BN254_PRIME, publicKeyY % BN254_PRIME, salt]);
  const value = BigInt(p.F.toObject(hash).toString()) % BN254_PRIME;
  return `0x${value.toString(16).padStart(64, "0")}`;
}

/**
 * Signs one action digest under the vault's Baby Jubjub key.
 *
 * The secret scalar never leaves the browser; the prover service receives only
 * the public key, the salt and this signature, so a compromised host can at
 * most re-submit the single action it was asked to prove.
 */
export async function signPerpsActionHash(
  spendingKey: Uint8Array,
  actionHashHex: string,
): Promise<{ r8x: string; r8y: string; s: string }> {
  const eddsa = await ensureEddsa();
  const signingKey = await getSigningKey(spendingKey);
  const signature = eddsa.signPoseidon(
    signingKey,
    eddsa.F.e(reduceToBn254(actionHashHex)),
  );
  return {
    r8x: BigInt(eddsa.F.toObject(signature.R8[0]).toString()).toString(),
    r8y: BigInt(eddsa.F.toObject(signature.R8[1]).toString()).toString(),
    s: BigInt(signature.S.toString()).toString(),
  };
}

export async function randomBytes32Hex(): Promise<string> {
  return ethers
    .hexlify(crypto.getRandomValues(new Uint8Array(32)))
    .toLowerCase();
}

export function encodePerpsModifyCollateralCallData(params: {
  accountId: bigint;
  synthMarketId: bigint;
  amountDelta: bigint;
}): string {
  const iface = new ethers.Interface([
    "function modifyCollateral(uint128 accountId,uint128 synthMarketId,int256 amountDelta)",
  ]);
  return iface.encodeFunctionData("modifyCollateral", [
    params.accountId,
    params.synthMarketId,
    params.amountDelta,
  ]);
}

export function encodePerpsCommitOrderCallData(params: {
  accountId: bigint;
  marketId: bigint;
  sizeDelta: bigint;
  settlementStrategyId: bigint;
  acceptablePrice: bigint;
  trackingCode: string;
  referrer: string;
}): string {
  const iface = new ethers.Interface([
    "function commitOrder((uint128 marketId,uint128 accountId,int128 sizeDelta,uint128 settlementStrategyId,uint256 acceptablePrice,bytes32 trackingCode,address referrer) commitment)",
  ]);
  return iface.encodeFunctionData("commitOrder", [
    [
      params.marketId,
      params.accountId,
      params.sizeDelta,
      params.settlementStrategyId,
      params.acceptablePrice,
      normalizeBytes32(params.trackingCode),
      params.referrer,
    ],
  ]);
}

export function encodePerpsCancelOrderCallData(params: {
  accountId: bigint;
}): string {
  const iface = new ethers.Interface([
    "function cancelOrder(uint128 accountId)",
  ]);
  return iface.encodeFunctionData("cancelOrder", [params.accountId]);
}

export function encodeAvantisOpenTradeCallData(params: {
  trader: string;
  pairIndex: bigint;
  tradeIndex: bigint;
  collateralInTradeUsdc: bigint;
  isLong: boolean;
  leverageX10: bigint;
  tpX10: bigint;
  slX10: bigint;
  openPriceX10: bigint;
  orderType: number;
  slippagePX10: bigint;
  timestamp: bigint;
}): string {
  const iface = new ethers.Interface([
    "function openTrade((address trader,uint256 pairIndex,uint256 index,uint256 initialPosToken,uint256 positionSizeUSDC,uint256 openPrice,bool buy,uint256 leverage,uint256 tp,uint256 sl,uint256 timestamp) t,uint8 orderType,uint256 slippageP)",
  ]);
  return iface.encodeFunctionData("openTrade", [
    [
      params.trader,
      params.pairIndex,
      params.tradeIndex,
      0n,
      params.collateralInTradeUsdc,
      params.openPriceX10,
      params.isLong,
      params.leverageX10,
      params.tpX10,
      params.slX10,
      params.timestamp,
    ],
    params.orderType,
    params.slippagePX10,
  ]);
}

export function encodeAvantisCloseTradeCallData(params: {
  pairIndex: bigint;
  tradeIndex: bigint;
  collateralToCloseUsdc: bigint;
}): string {
  const iface = new ethers.Interface([
    "function closeTradeMarket(uint256 pairIndex,uint256 tradeIndex,uint256 collateralToClose)",
  ]);
  return iface.encodeFunctionData("closeTradeMarket", [
    params.pairIndex,
    params.tradeIndex,
    params.collateralToCloseUsdc,
  ]);
}

export function encodeAvantisCancelLimitOrderCallData(params: {
  pairIndex: bigint;
  tradeIndex: bigint;
}): string {
  const iface = new ethers.Interface([
    "function cancelOpenLimitOrder(uint256 pairIndex,uint256 tradeIndex)",
  ]);
  return iface.encodeFunctionData("cancelOpenLimitOrder", [
    params.pairIndex,
    params.tradeIndex,
  ]);
}

export function encodeAvantisUpdateMarginCallData(params: {
  pairIndex: bigint;
  tradeIndex: bigint;
  updateType: 0 | 1;
  collateralChangeUsdc: bigint;
  priceUpdateData: string[];
  priceSourcing: 0 | 1;
}): string {
  const iface = new ethers.Interface([
    "function updateMargin(uint256 pairIndex,uint256 tradeIndex,uint8 updateType,uint256 collateralChange,bytes[] priceUpdateData,uint8 priceSourcing)",
  ]);
  return iface.encodeFunctionData("updateMargin", [
    params.pairIndex,
    params.tradeIndex,
    params.updateType,
    params.collateralChangeUsdc,
    params.priceUpdateData,
    params.priceSourcing,
  ]);
}

export function computePerpsProofActionHash(
  params: PerpsProofActionHashParams,
): string {
  const actionDataHash = computePerpsProofActionDataHash(params);
  return ethers.solidityPackedKeccak256(
    ["address", "uint256", "bytes32", "bytes32", "uint256", "uint64"],
    [
      params.vaultAddress,
      BigInt(params.chainId),
      params.actionDomain,
      actionDataHash,
      BigInt(params.nonce),
      BigInt(params.expiry),
    ],
  );
}

export function computePerpsProofActionDataHash(
  params: PerpsActionDataHashParams,
): string {
  const shouldUseMarginUsdcHash =
    params.actionDomain === PERPS_ACTION_DOMAINS.marginDeposit &&
    !!params.marginUsdcConfig;
  const shouldUseMarginWithdrawUsdcHash =
    params.actionDomain === PERPS_ACTION_DOMAINS.marginWithdraw &&
    !!params.marginWithdrawUsdcConfig;
  const shouldUseMarginReshieldHash =
    params.actionDomain === PERPS_ACTION_DOMAINS.marginReshield &&
    !!params.marginReshieldConfig;
  let actionDataHash: string;
  if (shouldUseMarginUsdcHash) {
    if (params.marginUsdcConfig!.minSUSDAmount <= 0n) {
      throw new Error("USDC margin deposit requires a positive minSUSDAmount");
    }
    const hasFees =
      BigInt(params.marginUsdcConfig!.protocolFeeBps) > 0n ||
      BigInt(params.marginUsdcConfig!.partnerFeeBps) > 0n;
    actionDataHash = hasFees
      ? ethers.keccak256(
          ethers.AbiCoder.defaultAbiCoder().encode(
            [
              "address",
              "address",
              "address",
              "address",
              "uint256",
              "uint128",
              "uint256",
              "address",
              "address",
              "uint16",
              "uint16",
            ],
            [
              params.perpsMarketProxy,
              params.marginUsdcConfig!.spotMarketProxy,
              params.marginUsdcConfig!.usdcToken,
              params.marginUsdcConfig!.sUsdToken,
              params.marginUsdcConfig!.usdcAmount,
              params.marginUsdcConfig!.synthMarketId,
              params.marginUsdcConfig!.minSUSDAmount,
              params.marginUsdcConfig!.protocolFeeRecipient,
              params.marginUsdcConfig!.partnerFeeRecipient,
              Number(params.marginUsdcConfig!.protocolFeeBps),
              Number(params.marginUsdcConfig!.partnerFeeBps),
            ],
          ),
        )
      : ethers.keccak256(
          ethers.AbiCoder.defaultAbiCoder().encode(
            [
              "address",
              "address",
              "address",
              "address",
              "uint256",
              "uint128",
              "uint256",
            ],
            [
              params.perpsMarketProxy,
              params.marginUsdcConfig!.spotMarketProxy,
              params.marginUsdcConfig!.usdcToken,
              params.marginUsdcConfig!.sUsdToken,
              params.marginUsdcConfig!.usdcAmount,
              params.marginUsdcConfig!.synthMarketId,
              params.marginUsdcConfig!.minSUSDAmount,
            ],
          ),
        );
  } else if (shouldUseMarginWithdrawUsdcHash) {
    if (params.marginWithdrawUsdcConfig!.minUSDCAmount <= 0n) {
      throw new Error("USDC margin withdraw requires a positive minUSDCAmount");
    }
    actionDataHash = ethers.keccak256(
      ethers.AbiCoder.defaultAbiCoder().encode(
        [
          "address",
          "address",
          "address",
          "address",
          "uint256",
          "uint128",
          "address",
          "uint256",
        ],
        [
          params.perpsMarketProxy,
          params.marginWithdrawUsdcConfig!.spotMarketProxy,
          params.marginWithdrawUsdcConfig!.usdcToken,
          params.marginWithdrawUsdcConfig!.sUsdToken,
          params.marginWithdrawUsdcConfig!.sUsdAmount,
          params.marginWithdrawUsdcConfig!.synthMarketId,
          params.marginWithdrawUsdcConfig!.recipient,
          params.marginWithdrawUsdcConfig!.minUSDCAmount,
        ],
      ),
    );
  } else if (shouldUseMarginReshieldHash) {
    actionDataHash = ethers.keccak256(
      ethers.AbiCoder.defaultAbiCoder().encode(
        [
          "address",
          "address",
          "uint256",
          "bytes32",
          "bytes32",
          "bytes32",
          "bytes32",
        ],
        [
          params.marginReshieldConfig!.shieldedPool,
          params.marginReshieldConfig!.usdcToken,
          params.marginReshieldConfig!.usdcAmount,
          normalizeBytes32(params.marginReshieldConfig!.outputCommitment),
          normalizeBytes32(params.marginReshieldConfig!.outputTokenHash),
          normalizeBytes32(params.marginReshieldConfig!.recoveryKey),
          ethers.keccak256(params.marginReshieldConfig!.encryptedNote),
        ],
      ),
    );
  } else {
    const callDataHash = ethers.keccak256(params.callData);
    actionDataHash = params.omitCallValueFromActionData
      ? ethers.keccak256(
          ethers.AbiCoder.defaultAbiCoder().encode(
            ["address", "bytes32"],
            [params.perpsMarketProxy, callDataHash],
          ),
        )
      : ethers.keccak256(
          ethers.AbiCoder.defaultAbiCoder().encode(
            ["address", "bytes32", "uint256"],
            [
              params.perpsMarketProxy,
              callDataHash,
              BigInt(params.callValueWei ?? 0),
            ],
          ),
        );
  }
  return actionDataHash;
}
